import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import SafeIcon from '../common/SafeIcon';
import { useLanguage } from '../context/LanguageContext';

const ApplicationProcess = () => {
  const { t } = useLanguage();
  const [isOpen, setIsOpen] = useState(false);

  return (
    <section className="section-padding bg-slate-50 border-t border-slate-200">
      <div className="container-custom">
        <div className="max-w-3xl mx-auto text-center mb-8">
          <motion.h2 
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-3xl md:text-4xl font-bold text-slate-900 mb-6"
          >
            {t.applicationProcess.title}
          </motion.h2>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.1 }}
            className="text-lg text-slate-600 leading-relaxed font-medium"
          >
            {t.applicationProcess.shortIntro}
          </motion.p>
        </div>

        <div className="max-w-4xl mx-auto bg-white rounded-2xl shadow-sm border border-slate-200 overflow-hidden">
          <button 
            onClick={() => setIsOpen(!isOpen)}
            className="w-full flex items-center justify-between p-6 md:p-8 bg-slate-50 hover:bg-slate-100 transition-colors focus:outline-none"
          >
            <h3 className="text-xl md:text-2xl font-bold text-slate-900 text-left">
              {t.applicationProcess.accordionTitle}
            </h3>
            <SafeIcon 
              name={isOpen ? "ChevronUp" : "ChevronDown"} 
              className="w-6 h-6 text-brand ml-4 flex-shrink-0" 
            />
          </button>

          <AnimatePresence>
            {isOpen && (
              <motion.div
                initial={{ height: 0, opacity: 0 }}
                animate={{ height: 'auto', opacity: 1 }}
                exit={{ height: 0, opacity: 0 }}
                className="overflow-hidden"
              >
                <div className="p-6 md:p-8 border-t border-slate-200">
                  <p className="text-slate-600 mb-8 leading-relaxed">
                    {t.applicationProcess.intro}
                  </p>
                  
                  {/* Timeline */}
                  <div className="space-y-8 md:space-y-0 md:grid md:grid-cols-2 md:gap-x-8 md:gap-y-10 mb-12">
                    {t.applicationProcess.steps.map((step, idx) => (
                      <div key={idx} className="relative flex md:block items-start gap-4 group">
                        <div className="flex-shrink-0 w-10 h-10 rounded-full bg-brand text-white flex items-center justify-center font-bold relative z-10 shadow-sm border-2 border-white md:mb-5 group-hover:scale-110 transition-transform duration-300">
                          {idx + 1}
                        </div>
                        <div>
                          <h4 className="text-lg font-bold text-slate-900 mb-2">{step.title}</h4>
                          <p className="text-slate-600 text-sm leading-relaxed">{step.desc}</p>
                        </div>
                      </div>
                    ))}
                  </div>

                  {/* Summary Card */}
                  <div className="bg-slate-50 rounded-xl border border-slate-200 p-6">
                    <div className="flex items-center gap-3 mb-4">
                      <SafeIcon name="ClipboardCheck" className="w-6 h-6 text-brand" />
                      <h4 className="text-xl font-bold text-slate-900">{t.applicationProcess.summaryCard.title}</h4>
                    </div>
                    
                    <ul className="space-y-3 mb-6">
                      {t.applicationProcess.summaryCard.items.map((item, idx) => (
                        <li key={idx} className="flex items-start gap-3 text-slate-700 text-sm">
                          <SafeIcon name="CheckCircle2" className="w-5 h-5 text-green-500 flex-shrink-0" />
                          <span>{item}</span>
                        </li>
                      ))}
                    </ul>
                    
                    <div className="bg-white p-4 rounded-lg text-xs text-slate-500 border border-slate-100 flex gap-3">
                      <SafeIcon name="Info" className="w-4 h-4 flex-shrink-0 text-slate-400 mt-0.5" />
                      <p>{t.applicationProcess.summaryCard.disclosure}</p>
                    </div>
                  </div>
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </div>
    </section>
  );
};

export default ApplicationProcess;